import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { ItemTypeEnum } from '@galabid/gb-dashboard/item/data-access'
import { GbDashboardSharedUiModule } from '@galabid/gb-dashboard/shared/ui'
import { GbDashboardSharedUtilsModule } from '@galabid/gb-dashboard/shared/utils'
import { GbColorService } from '@galabid/shared/utils/services'
import { Actions, ofType } from '@ngrx/effects'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { GbDashboardPusherService } from 'libs/gb-dashboard/core/util/src/lib/gb-dashboard-pusher-service/gb-dashboard-pusher.service'
import {
  addHonourRollActivityForRealtime,
  deleteHonourRollActivityForRealtime,
  updateSelectedHonourRollWidgetSuccess,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import {
  HonourRollActivity,
  HonourRollWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbCurrencyPipe } from 'libs/gb-dashboard/shared/utils/src/lib/gb-currency-pipe/gb-currency.pipe'
import { SharedUiModule } from 'libs/shared/ui/src/lib/shared-ui.module'
import { filter, tap } from 'rxjs/operators'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { GbHonourRollStore } from './gb-presenter-widget-honour-roll.store'

@Component({
  selector: 'gb-presenter-widget-honour-roll',
  imports: [
    CommonModule,
    TranslateModule,
    GbDashboardSharedUtilsModule,
    GbDashboardSharedUiModule,
    GbPresenterWidgetPlaceholderComponent,
    MatProgressSpinnerModule,
    SharedUiModule,
  ],
  templateUrl: './gb-presenter-widget-honour-roll.component.html',
  styleUrl: './gb-presenter-widget-honour-roll.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetHonourRollComponent implements OnDestroy {
  private readonly honourRollStore = inject(GbHonourRollStore)
  private readonly gbColorService = inject(GbColorService)
  private readonly pusherService = inject(GbDashboardPusherService)
  private readonly colorResolver = inject(GbPresenterColorResolverService)
  private readonly gbCurrencyPipe = inject(GbCurrencyPipe)
  private readonly translateService = inject(TranslateService)
  private readonly actions$ = inject(Actions)
  private readonly destroyRef = inject(DestroyRef)

  readonly inPreviewMode = input(false)
  readonly testData = input(false)
  readonly canvasWidth = input(0)
  readonly canvasHeight = input(0)
  readonly isTemplateMode = input(false)
  readonly honourRollWidget = input.required<HonourRollWidget>()

  readonly loading = toSignal(this.honourRollStore.loading$, { initialValue: false })
  private readonly storeActivities = toSignal(this.honourRollStore.activities$, { initialValue: [] })

  private readonly currentCycleActivities = signal<HonourRollActivity[]>([])
  private readonly nextCycleActivities = signal<HonourRollActivity[]>([])
  private readonly pendingActivities = signal<HonourRollActivity[]>([])
  private currentCycleRefreshTimer?: ReturnType<typeof setTimeout>

  private readonly limit = computed(() => this.honourRollWidget()?.latest_activities ?? 1)

  private readonly itemTypeDisplayValues = computed(() => {
    return {
      [ItemTypeEnum.Donation as string]: this.translateService.instant('ITEM_TYPES.DONATION'),
      [ItemTypeEnum.Bidding as string]: this.translateService.instant('ITEM_TYPES.BID'),
      [ItemTypeEnum.Live as string]: this.translateService.instant('ITEM_TYPES.BID'),
      [ItemTypeEnum.Sealed as string]: this.translateService.instant('ITEM_TYPES.SEALED'),
      [ItemTypeEnum.Raffle as string]: this.translateService.instant('ITEM_TYPES.RAFFLE'),
      [ItemTypeEnum.Purchase as string]: this.translateService.instant('ITEM_TYPES.PURCHASE'),
      ['bidding_purchase']: this.translateService.instant('ITEM_TYPES.BIDDING_PURCHASE'),
    }
  })

  readonly isHorizontalScroll = computed(() => {
    const transition = this.honourRollWidget()?.transition || 'scroll-right'
    return transition === 'scroll-left' || transition === 'scroll-right'
  })

  readonly isReverseDirection = computed(() => {
    const transition = this.honourRollWidget()?.transition || 'scroll-right'
    return transition === 'scroll-right' || transition === 'scroll-bottom'
  })

  readonly tickerDuration = computed(() => `${this.honourRollWidget()?.slide_interval || 3}s`)
  readonly rows = computed(() => this.honourRollWidget()?.rows ?? 1)
  readonly columns = computed(() => this.honourRollWidget()?.columns ?? 1)

  readonly gridTemplateColumns = computed(() => {
    return `repeat(${this.columns()}, minmax(0, 1fr))`
  })

  readonly gridTemplateRows = computed(() => {
    return `repeat(${this.rows()}, minmax(0, 1fr))`
  })

  readonly pageSize = computed(() => {
    return (this.rows() as number) * (this.columns() as number)
  })

  readonly pages = computed<HonourRollActivity[][]>(() => this.chunkIntoPages(this.activities()))

  private readonly nextPages = computed<HonourRollActivity[][]>(() => {
    return this.testData() ? this.pages() : this.chunkIntoPages(this.nextCycleActivities())
  })

  readonly pagesLoop = computed<HonourRollActivity[][]>(() => {
    const pages = this.pages()
    if (pages.length === 0) return []
    return [...pages, ...this.nextPages(), ...pages.slice(0, 1)]
  })

  readonly totalPages = computed(() => Math.max(1, this.pages().length))

  private readonly animationPageCount = computed(() => {
    const pages = this.pages()
    if (pages.length === 0) return 0
    return pages.length + this.nextPages().length
  })

  readonly carouselDurationMs = computed(() => {
    const pageCount = this.animationPageCount()
    if (!pageCount) return 0
    const intervalSeconds = Math.max(1, Number(this.honourRollWidget()?.slide_interval ?? 10))
    return intervalSeconds * pageCount * 1000
  })

  readonly carouselTranslatePercent = computed(() => {
    const pageCount = this.animationPageCount()
    return pageCount ? pageCount * 100 : 0
  })

  readonly isScrollLeft = computed(() => this.honourRollWidget()?.transition === 'scroll-left')
  readonly isScrollRight = computed(() => this.honourRollWidget()?.transition === 'scroll-right')
  readonly isScrollTop = computed(() => this.honourRollWidget()?.transition === 'scroll-top')
  readonly isScrollBottom = computed(() => this.honourRollWidget()?.transition === 'scroll-bottom')

  readonly cardLayoutDirection = computed(() => {
    const layoutDirection = this.honourRollWidget()?.layout_direction
    if (layoutDirection === 'horizontal' || layoutDirection === 'vertical') return layoutDirection
    return this.isHorizontalScroll() ? 'vertical' : 'horizontal'
  })

  readonly activities = computed<HonourRollActivity[]>(() => {
    return this.testData() ? this.generateMockActivities().slice(0, this.limit()) : this.currentCycleActivities()
  })

  readonly showEmptyState = computed(() => !this.loading() && this.activities().length === 0)

  readonly backgroundColor = computed(
    () => this.colorResolver.resolve(this.honourRollWidget()?.table_background_color) || undefined,
  )
  readonly cardBackgroundColor = computed(
    () => this.colorResolver.resolve(this.honourRollWidget()?.table_accent_color) || undefined,
  )
  readonly primaryColor = computed(
    () => this.colorResolver.resolve(this.honourRollWidget()?.table_primary_color) || undefined,
  )
  readonly primaryColorContrast = computed(() => this.gbColorService.getContrast(this.primaryColor() || '#fff'))
  readonly opacity = computed(() => this.honourRollWidget()?.opacity ?? 1)
  readonly fontSizeScale = computed(() => (this.honourRollWidget()?.font_size ?? 20) / 22)
  readonly fontFamily = computed(
    () => this.honourRollWidget()?.font_family || this.colorResolver.globalFontFamily() || 'inherit',
  )
  readonly fontWeight = computed(() => this.honourRollWidget()?.font_weight || 'normal')
  readonly textAlign = computed(() => this.honourRollWidget()?.text_align || 'left')
  readonly borderWidth = computed(() => (this.honourRollWidget()?.border_width || 0) / 10)
  readonly borderColor = computed(
    () => this.colorResolver.resolve(this.honourRollWidget()?.border_color) || 'transparent',
  )
  readonly borderStyle = computed(() =>
    (this.honourRollWidget()?.border_width || 0) > 0 ? this.honourRollWidget()?.border_style || 'solid' : 'none',
  )

  constructor() {
    const widgetUuid = computed(() => this.honourRollWidget()?.uuid)
    const latestActivities = computed(() => this.honourRollWidget()?.latest_activities)

    effect(() => {
      const isTest = this.testData()
      const isTemplate = this.isTemplateMode()
      const uuid = widgetUuid()

      if (uuid) {
        this.pusherService.unsubscribeFromHonourRollActivityChannels()
        this.pusherService.initHonourRollActivityPusher(uuid)

        if (!isTest && !isTemplate) {
          this.resetCarouselState()
          this.honourRollStore.clearActivities()
          this.honourRollStore.loadHonourRollActivities({
            widget: uuid,
            limit: latestActivities() ?? 0,
            ordering: '-created',
          })
        }
      }
    })

    effect(() => {
      const latest = this.storeActivities()

      if (this.testData() || this.isTemplateMode()) return

      if (this.carouselDurationMs() === 0) {
        const list = latest.slice(0, this.limit())
        this.currentCycleActivities.set(list)
        this.nextCycleActivities.set(list)
        this.pendingActivities.set([])
      } else if (this.currentCycleActivities().length === 0) {
        const list = latest.slice(0, this.limit())
        this.currentCycleActivities.set(list)
        this.nextCycleActivities.set(list)
      }
    })

    this.actions$
      .pipe(
        ofType(updateSelectedHonourRollWidgetSuccess),
        filter(() => !this.testData() && !this.isTemplateMode()),
        filter(({ update }) => update.id === widgetUuid()),
        tap(() => {
          const uuid = widgetUuid()
          if (!uuid) return

          this.resetCarouselState()
          this.honourRollStore.clearActivities()
          this.honourRollStore.loadHonourRollActivities({
            widget: uuid,
            limit: latestActivities() ?? 0,
            ordering: '-created',
          })
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(addHonourRollActivityForRealtime),
        filter(() => !this.testData() && !this.isTemplateMode()),
        filter(({ data }) => data.widget_uuid === widgetUuid()),
        tap(({ data }) => {
          this.honourRollStore.addHonourRollActivity(data)
          this.pendingActivities.update((queue) => [...queue, data])
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(deleteHonourRollActivityForRealtime),
        filter(() => !this.testData() && !this.isTemplateMode()),
        tap(({ uuid }) => {
          this.honourRollStore.removeHonourRollActivity(uuid)
          this.pendingActivities.update((queue) => queue.filter((a) => a.uuid !== uuid))
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  private resetCarouselState(): void {
    if (this.currentCycleRefreshTimer) {
      clearTimeout(this.currentCycleRefreshTimer)
      this.currentCycleRefreshTimer = undefined
    }

    this.currentCycleActivities.set([])
    this.nextCycleActivities.set([])
    this.pendingActivities.set([])
  }

  onCarouselCycleComplete(event: AnimationEvent): void {
    if (event.target !== event.currentTarget) return
    if (this.testData() || this.isTemplateMode()) return

    const next = this.buildNextCycle()
    this.nextCycleActivities.set(next)
    this.pendingActivities.set([])

    this.scheduleCurrentCycleRefresh()
  }

  private buildNextCycle(): HonourRollActivity[] {
    const pending = this.pendingActivities()
    const latest = this.storeActivities()
    const limit = this.limit()

    if (!pending.length) return latest.slice(0, limit)

    const pendingUuids = new Set(pending.map((a) => a.uuid))
    const fill = latest.filter((a) => !pendingUuids.has(a.uuid)).slice(0, Math.max(0, limit - pending.length))
    return [...pending, ...fill]
  }

  private scheduleCurrentCycleRefresh(): void {
    clearTimeout(this.currentCycleRefreshTimer)

    const duration = this.carouselDurationMs()
    const currentPageCount = this.pages().length
    const totalAnimationPages = this.animationPageCount()
    if (!duration || !totalAnimationPages) return

    const windowStart = currentPageCount / totalAnimationPages
    const windowEnd = (totalAnimationPages - 1) / totalAnimationPages
    if (windowEnd < windowStart) return // next cycle too short, no safe window - the wrap refresh covers it

    const refreshAt = duration * ((windowStart + windowEnd) / 2)
    this.currentCycleRefreshTimer = setTimeout(() => this.refreshCurrentCycleOffscreen(), refreshAt)
  }

  private refreshCurrentCycleOffscreen(): void {
    if (this.testData() || this.isTemplateMode() || this.carouselDurationMs() === 0) return

    const requiredCount = this.currentCycleActivities().length
    const pending = this.pendingActivities().slice(0, requiredCount)
    const pendingUuids = new Set(pending.map((a) => a.uuid))
    const fill = this.storeActivities()
      .filter((a) => !pendingUuids.has(a.uuid))
      .slice(0, requiredCount - pending.length)
    const refreshed = [...pending, ...fill]

    if (refreshed.length !== requiredCount) return

    this.currentCycleActivities.set(refreshed)
    this.pendingActivities.update((queue) => queue.slice(pending.length))
  }

  private chunkIntoPages(activities: HonourRollActivity[]): HonourRollActivity[][] {
    const pageSize = this.pageSize()
    if (!pageSize || !activities.length) return []

    const remainder = activities.length % pageSize
    const padded =
      remainder && activities.length > pageSize
        ? [...activities, ...activities.slice(0, pageSize - remainder)]
        : activities

    const pages: HonourRollActivity[][] = []
    for (let i = 0; i < padded.length; i += pageSize) {
      pages.push(padded.slice(i, i + pageSize))
    }
    return pages
  }

  getContrastColor(color: string | null | undefined): string {
    return color ? this.gbColorService.getContrast(color) : '#000000DE'
  }

  getActivityTypeDisplayValue(activity: HonourRollActivity): string {
    const activityTypeKey = activity.is_bidding_purchase ? 'bidding_purchase' : activity.item_type
    return this.itemTypeDisplayValues()[activityTypeKey] || activityTypeKey
  }

  getActivityDetailsText(activity: HonourRollActivity): string {
    const showName = this.honourRollWidget()?.show_item_name ?? true
    const showAlias = this.honourRollWidget()?.show_item_alias ?? true

    let itemDisplay = ''
    if (showName && activity.item_name) itemDisplay = `<b>${activity.item_name}</b>`
    if (showAlias && activity.item_alias) {
      itemDisplay = itemDisplay ? `${itemDisplay} (#${activity.item_alias})` : `<b>#${activity.item_alias}</b>`
    }

    const formattedAmount = this.gbCurrencyPipe.transform(activity.amount)
    const amount = `<b>${formattedAmount}</b>`
    const quantity = `<b>${activity.quantity}</b>`

    if (activity.is_bidding_purchase) {
      const q = activity.quantity ?? 1
      const purchaseQuantity = `<b>${q}</b>`
      const withItemKey =
        q === 1
          ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITH_ITEM_SINGULAR'
          : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITH_ITEM'
      const withoutItemKey =
        q === 1
          ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITHOUT_ITEM_SINGULAR'
          : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITHOUT_ITEM'

      return itemDisplay
        ? this.translateService.instant(withItemKey, {
            quantity: purchaseQuantity,
            item: itemDisplay,
            amount,
          })
        : this.translateService.instant(withoutItemKey, {
            quantity: purchaseQuantity,
            amount,
          })
    }

    switch (activity.item_type) {
      case ItemTypeEnum.Bidding:
        return itemDisplay
          ? this.translateService.instant('PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.BIDDING.WITH_ITEM', {
              amount,
              item: itemDisplay,
            })
          : this.translateService.instant('PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.BIDDING.WITHOUT_ITEM', {
              amount,
            })
      case ItemTypeEnum.Purchase: {
        const q = activity.quantity ?? 1
        const purchaseQuantity = `<b>${q}</b>`
        const withItemKey =
          q === 1
            ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITH_ITEM_SINGULAR'
            : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITH_ITEM'
        const withoutItemKey =
          q === 1
            ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITHOUT_ITEM_SINGULAR'
            : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.PURCHASE.WITHOUT_ITEM'

        return itemDisplay
          ? this.translateService.instant(withItemKey, {
              quantity: purchaseQuantity,
              item: itemDisplay,
              amount,
            })
          : this.translateService.instant(withoutItemKey, {
              quantity: purchaseQuantity,
              amount,
            })
      }
      case ItemTypeEnum.Raffle: {
        const isPlural = (activity.quantity ?? 0) > 1
        const keyPrefix = isPlural
          ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.RAFFLE.WITH_ITEM_PLURAL'
          : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.RAFFLE.WITH_ITEM'
        const withoutKeyPrefix = isPlural
          ? 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.RAFFLE.WITHOUT_ITEM_PLURAL'
          : 'PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.RAFFLE.WITHOUT_ITEM'

        return itemDisplay
          ? this.translateService.instant(keyPrefix, {
              quantity,
              item: itemDisplay,
            })
          : this.translateService.instant(withoutKeyPrefix, {
              quantity,
            })
      }
      case ItemTypeEnum.Donation:
        return itemDisplay
          ? this.translateService.instant('PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.DONATION.WITH_ITEM', {
              amount,
              item: itemDisplay,
            })
          : this.translateService.instant('PRESENTER.HONOUR_ROLL_WIDGET.ACTIVITY_DETAILS.DONATION.WITHOUT_ITEM', {
              amount,
            })
      default:
        return ''
    }
  }

  private generateMockActivities(): HonourRollActivity[] {
    const totalSlides = this.honourRollWidget()?.latest_activities ?? 5
    const mockActivities: HonourRollActivity[] = [
      {
        uuid: 'mock-1',
        item_alias: '101',
        item_name: 'Vintage Watch Collection',
        item_type: 'bidding',
        is_bidding_purchase: false,
        profile_full_name: 'John Smith',
        quantity: 1,
        amount: 250,
        created: new Date().toISOString(),
        widget_uuid: '',
        item_image: null,
      },
      {
        uuid: 'mock-2',
        item_alias: '205',
        item_name: 'Weekend Getaway Package',
        item_type: 'purchase',
        is_bidding_purchase: false,
        profile_full_name: 'Sarah Johnson',
        quantity: 2,
        amount: 180,
        created: new Date().toISOString(),
        widget_uuid: '',
        item_image: null,
      },
      {
        uuid: 'mock-3',
        item_alias: '310',
        item_name: 'Charity Dinner Tickets',
        item_type: 'raffle',
        is_bidding_purchase: false,
        profile_full_name: 'Michael Brown',
        quantity: 5,
        amount: 75,
        created: new Date().toISOString(),
        widget_uuid: '',
        item_image: null,
      },
      {
        uuid: 'mock-4',
        item_alias: '425',
        item_name: 'Community Support Fund',
        item_type: 'donation',
        is_bidding_purchase: false,
        profile_full_name: 'Emily Davis',
        quantity: 1,
        amount: 500,
        created: new Date().toISOString(),
        widget_uuid: '',
        item_image: null,
      },
    ]

    return Array.from({ length: totalSlides }, (_, i) => ({
      ...mockActivities[i % mockActivities.length],
      uuid: `mock-${i}`,
    }))
  }

  ngOnDestroy(): void {
    clearTimeout(this.currentCycleRefreshTimer)
    this.pusherService.unsubscribeFromHonourRollActivityChannels()
  }
}
